import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Mail,
  Phone,
  Globe,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  AlertCircle,
  MapPin,
  Shield,
  Plus,
  Check,
  X,
  Pencil,
  UserX,
  Gamepad2,
  LifeBuoy,
  Headphones,
  MessageSquare,
  Gavel,
  Ban,
  Car,
  Map,
  Server,
  Crown,
  Sparkles,
  ShieldCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '../../contexts/AuthContext';
import LogoCapital from '../../assets/logo-capital.jpg';

/**
 * Página de Completar Perfil
 * Exibe dados extraídos do Discord e solicita informações faltantes (Telefone, Idioma)
 */
const CompleteProfilePage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    discordId: '',
    name: '',
    email: '',
    avatar: '',
    phone: '',
    language: 'pt-BR',
    cities: [],
    joinDate: '',
    isBooster: false
  });

  // Estado para edição inline
  const [editingIndex, setEditingIndex] = useState(null);
  const [tempEditData, setTempEditData] = useState(null);

  // Carrega dados do Discord passados pelo Login
  useEffect(() => {
    if (location.state?.discordData) {
      const data = location.state.discordData;
      setFormData(prev => ({
        ...prev,
        discordId: data.discordId,
        name: data.name,
        email: data.email,
        avatar: data.avatar,
        cities: data.cities || [],
        joinDate: data.joinDate,
        isBooster: data.isBooster
      }));
    } else {
      // Se não tiver dados, volta pro login (segurança)
      navigate('/login');
    }
  }, [location.state, navigate]);

  // Passos do formulário
  const steps = [
    {
      id: 1,
      title: 'Identidade',
      description: 'Verifique seus dados',
      icon: User
    },
    {
      id: 2,
      title: 'Contato',
      description: 'Informe seu telefone',
      icon: Phone
    },
    {
      id: 3,
      title: 'Resumo',
      description: 'Confirmar cadastro',
      icon: Shield
    }
  ];

  // Função para atualizar dados
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError('');
  };

  // Formatar telefone
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  // Validação e Avanço
  const handleNext = () => {
    if (currentStep === 1) {
      // Validação: Não permitir 'PENDENTE'
      const hasPending = formData.cities.some(c => c.playerId === 'PENDENTE' || !c.playerId);
      if (hasPending) {
        setError('Por favor, corrija os IDs pendentes antes de continuar.');
        return;
      }
    }

    if (currentStep === 2) {
      if (!formData.phone || formData.phone.length < 14) {
        setError('Por favor, insira um telefone válido.');
        return;
      }
    }

    if (currentStep < steps.length) {
      setCurrentStep(prev => prev + 1);
      setError('');
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setError('');
    }
  };

  // Handlers para edição inline
  const startEditing = (index, city) => {
    setEditingIndex(index);
    setTempEditData({ ...city });
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setTempEditData(null);
  };

  const saveEditing = (index) => {
    if (!tempEditData.characterName || !tempEditData.playerId) {
      setError('Preencha todos os campos.');
      return;
    }

    const newCities = [...formData.cities];
    // Mark as manually verified so backend knows to create a ticket
    newCities[index] = {
      ...tempEditData,
      playerId: tempEditData.playerId.replace('#', ''),
      isManual: true // FLAGGED FOR MANUAL REVIEW
    };
    setFormData(prev => ({ ...prev, cities: newCities }));
    setEditingIndex(null);
    setTempEditData(null);
    setError('');
  };

  const setNoCharacter = (index) => {
    const newCities = [...formData.cities];
    newCities[index] = {
      ...newCities[index],
      characterName: formData.name, // Usa o nome do Discord
      playerId: 'SEM_PERSONAGEM',
      isManual: false // No verification needed
    };
    setFormData(prev => ({ ...prev, cities: newCities }));
    if (editingIndex === index) cancelEditing();
  };

  // Submissão Final
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const result = await register(formData);
      if (result.success) {
        navigate('/dashboard'); // Redireciona direto para o dashboard após registro
      } else {
        setError(result.error || 'Erro ao completar cadastro.');
      }
    } catch (err) {
      setError('Erro interno. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Ícones para o background animado
  const backgroundIcons = [
    { Icon: LifeBuoy, color: "text-blue-500" },
    { Icon: Shield, color: "text-red-500" },
    { Icon: Gamepad2, color: "text-purple-500" },
    { Icon: Headphones, color: "text-cyan-500" },
    { Icon: Gavel, color: "text-orange-500" },
    { Icon: Car, color: "text-yellow-500" },
    { Icon: MessageSquare, color: "text-green-500" },
    { Icon: Ban, color: "text-rose-500" },
    { Icon: Map, color: "text-emerald-500" },
    { Icon: Server, color: "text-indigo-500" },
    { Icon: Crown, color: "text-amber-500" },
    { Icon: Sparkles, color: "text-pink-500" },
  ];

  const infiniteIcons = [...backgroundIcons, ...backgroundIcons, ...backgroundIcons];

  // Renderização do Conteúdo do Passo
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Card de Perfil Discord */}
            <div className="bg-white/5 rounded-2xl p-5 border border-white/10 flex items-center gap-5 backdrop-blur-md">
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-[#5865F2] flex items-center justify-center text-2xl font-bold text-white overflow-hidden ring-4 ring-[#5865F2]/20 shadow-xl">
                  {formData.avatar ? (
                    <img src={`https://cdn.discordapp.com/avatars/${formData.discordId}/${formData.avatar}.png`} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    formData.name.charAt(0).toUpperCase()
                  )}
                </div>
                {formData.isBooster && (
                  <div className="absolute -bottom-1 -right-1 bg-pink-500 rounded-full p-1 border-2 border-[#0a0a0a]" title="Server Booster">
                    <Sparkles className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-0.5">{formData.name}</h3>
                <p className="text-sm text-blue-200/60 font-medium">{formData.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[10px] uppercase font-bold tracking-wider bg-[#5865F2]/20 text-[#5865F2] px-2 py-0.5 rounded-full border border-[#5865F2]/30 shadow-sm shadow-[#5865F2]/10">
                    Discord Verificado
                  </span>
                </div>
              </div>
            </div>

            {/* Lista de Cidades (Com Edição) */}
            <div>
              <h4 className="text-xs font-bold text-blue-300/80 uppercase tracking-widest mb-4 flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5" />
                Cidades e Personagens
              </h4>

              <div className="space-y-3">
                {formData.cities.map((city, index) => {
                  const isPending = city.playerId === 'PENDENTE';
                  const isEditing = editingIndex === index;
                  const isNoCharacter = city.playerId === 'SEM_PERSONAGEM';
                  const showInputs = isPending || isEditing;
                  const displayData = isEditing ? tempEditData : city;

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`rounded-xl p-4 border ${isPending ? 'bg-yellow-500/5 border-yellow-500/30' : 'bg-white/5 border-white/5'} flex flex-col gap-3 transition-all duration-300 hover:bg-white/10`}
                    >
                      <div className="flex justify-between items-center">
                        <p className="text-blue-400 text-xs font-bold uppercase tracking-wider">{city.city}</p>
                      </div>

                      {showInputs ? (
                        <div className="space-y-3">
                          <div className="flex gap-2">
                            <Input
                              placeholder="Nome do Personagem"
                              value={isEditing ? tempEditData.characterName : displayData.characterName}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (isEditing) setTempEditData({ ...tempEditData, characterName: val });
                                else startEditing(index, { ...city, characterName: val });
                              }}
                              className="bg-black/40 border-white/10 text-sm h-9 flex-1 text-white placeholder:text-white/20 focus:border-blue-500/50 transition-colors"
                            />
                            <Input
                              placeholder="ID"
                              value={isEditing ? tempEditData.playerId : (displayData.playerId === 'PENDENTE' ? '' : displayData.playerId)}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (isEditing) setTempEditData({ ...tempEditData, playerId: val });
                                else startEditing(index, { ...city, playerId: val });
                              }}
                              className="bg-black/40 border-white/10 w-24 text-sm h-9 text-white placeholder:text-white/20 focus:border-blue-500/50 transition-colors"
                            />
                          </div>

                          <div className="flex justify-between items-center pt-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setNoCharacter(index)}
                              className="text-[10px] text-white h-7 px-3 bg-gradient-to-r from-pink-500/80 to-purple-600/80 hover:from-pink-500 hover:to-purple-600 rounded-lg shadow-lg shadow-pink-500/20 border-none transition-all hover:scale-105"
                            >
                              <UserX className="h-3 w-3 mr-1.5" />
                              Não tenho personagem
                            </Button>

                            <div className="flex gap-2">
                              {isEditing && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={cancelEditing}
                                  className="h-7 w-7 p-0 text-red-400 hover:bg-red-500/10 rounded-lg"
                                  title="Cancelar"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                onClick={() => saveEditing(index)}
                                className="h-7 px-3 bg-emerald-500/80 hover:bg-emerald-500 text-white text-[10px] uppercase font-bold tracking-wide rounded-lg shadow-lg shadow-emerald-500/20 transition-all hover:scale-105"
                              >
                                <Check className="h-3 w-3 mr-1" />
                                Salvar
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between items-end">
                          <div>
                            <p className="text-white font-medium text-lg">
                              {isNoCharacter ? <span className="text-white/40 italic text-sm">Visitante (Sem personagem)</span> : city.characterName}
                            </p>
                          </div>
                          {!isNoCharacter && (
                            <div className="text-right bg-black/40 px-3 py-1 rounded-lg border border-white/5">
                              <span className="text-[10px] text-white/40 block uppercase tracking-wider mb-0.5">ID Cidadão</span>
                              <span className="text-sm font-mono text-emerald-400 font-bold">#{city.playerId}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {isPending && !isEditing && (
                        <p className="text-[10px] text-yellow-500 flex items-center gap-1.5 bg-yellow-500/10 p-2 rounded-lg border border-yellow-500/20">
                          <AlertCircle className="h-3 w-3" />
                          <span className="font-medium">Identificação pendente. Edite para corrigir.</span>
                        </p>
                      )}
                    </motion.div>
                  );
                })}

                {formData.cities.length === 0 && (
                  <div className="text-center py-8 bg-white/5 rounded-xl border border-white/5 border-dashed">
                    <p className="text-white/40 text-sm">Nenhuma cidade vinculada.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="bg-white/5 p-6 rounded-2xl border border-white/10 text-center">
              <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-500/20">
                <Phone className="h-8 w-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Contato Rápido</h3>
              <p className="text-sm text-blue-200/60 mb-6 max-w-xs mx-auto">
                Precisamos do seu número para comunicações importantes sobre seus tickets e solicitações.
              </p>

              <div className="relative">
                <Input
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', formatPhone(e.target.value))}
                  placeholder="(11) 99999-9999"
                  className="bg-black/40 border-white/10 text-white text-center text-lg h-14 tracking-widest placeholder:tracking-normal placeholder:text-white/20 focus:border-blue-500/50 transition-colors rounded-xl"
                  maxLength={15}
                />
              </div>
              <p className="text-xs text-white/30 mt-4 flex items-center justify-center gap-1.5">
                <ShieldCheck className="h-3 w-3" />
                Seus dados estão protegidos e não serão compartilhados.
              </p>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-400" />
                Resumo do Cadastro
              </h3>

              <div className="grid grid-cols-1 gap-4 mb-6">
                {/* Perfil */}
                <div className="bg-black/20 rounded-xl p-4 border border-white/5 flex flex-col gap-1">
                  <p className="text-[10px] text-white/40 uppercase tracking-wider font-bold">Identidade</p>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="w-8 h-8 rounded-full bg-[#5865F2] flex items-center justify-center text-white font-bold overflow-hidden">
                      {formData.avatar ? (
                        <img src={`https://cdn.discordapp.com/avatars/${formData.discordId}/${formData.avatar}.png`} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        formData.name.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{formData.name}</p>
                      <p className="text-white/60 text-xs">{formData.email}</p>
                    </div>
                  </div>
                </div>

                {/* Contato */}
                <div className="bg-black/20 rounded-xl p-4 border border-white/5 flex flex-col gap-1">
                  <p className="text-[10px] text-white/40 uppercase tracking-wider font-bold">Contato</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Phone className="h-3.5 w-3.5 text-blue-400" />
                    <p className="text-white font-medium text-sm">{formData.phone}</p>
                  </div>
                </div>
              </div>

              {/* Cidades Resumo */}
              <div>
                <p className="text-[10px] text-white/40 uppercase tracking-wider font-bold mb-3">Cidades Vinculadas</p>
                <div className="space-y-2">
                  {formData.cities.map((city, index) => (
                    <div key={index} className="bg-black/20 rounded-xl p-3 border border-white/5 flex justify-between items-center px-4">
                      <div>
                        <p className="text-blue-400 text-[10px] font-bold uppercase tracking-wider mb-0.5">{city.city}</p>
                        <p className="text-white font-medium text-sm">
                          {city.playerId === 'SEM_PERSONAGEM' ? <span className="italic text-white/40">Sem personagem</span> : city.characterName}
                        </p>
                      </div>
                      <div className="text-right">
                        {city.playerId !== 'SEM_PERSONAGEM' && (
                          <div className="bg-white/5 px-2 py-1 rounded border border-white/5">
                            <span className="text-xs font-mono text-emerald-400 font-bold">#{city.playerId}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {formData.cities.length === 0 && (
                    <p className="text-white/30 text-xs italic text-center py-2">Nenhuma cidade vinculada.</p>
                  )}
                </div>

                {/* Aviso de Verificação Manual */}
                {formData.cities.some(c => c.isManual) && (
                  <div className="mt-4 p-3 rounded-xl bg-orange-500/10 border border-orange-500/20 text-orange-200 text-xs flex items-start gap-3">
                    <Shield className="h-5 w-5 shrink-0 text-orange-400 mt-0.5" />
                    <div>
                      <p className="font-bold text-orange-400 mb-1">Verificação Necessária</p>
                      <p>
                        Você informou alguns dados manualmente. Para segurança da comunidade, o acesso de tickets (suporte) a esses personagens ficará <strong>Pendente</strong> até que nossa equipe aprove o vínculo. Um ticket foi criado automaticamente em seu nome, consulte ele na seção (Meus Tickets).
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 overflow-hidden relative selection:bg-blue-500/30">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Orbs */}
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(59,130,246,0.1),transparent_70%)]" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_80%_80%,rgba(168,85,247,0.1),transparent_70%)]" />

        {/* Scrolling Icons Rows */}
        <div className="absolute inset-0 flex flex-col justify-between opacity-5 select-none rotate-[-12deg] scale-125">
          {/* Row 1 - Left to Right */}
          <motion.div
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            className="flex gap-24 whitespace-nowrap"
          >
            {infiniteIcons.map((item, index) => (
              <item.Icon key={`r1-${index}`} className={`w-24 h-24 ${item.color}`} />
            ))}
          </motion.div>

          {/* Row 2 - Right to Left */}
          <motion.div
            animate={{ x: ["-50%", "0%"] }}
            transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
            className="flex gap-32 whitespace-nowrap ml-12"
          >
            {infiniteIcons.map((item, index) => (
              <item.Icon key={`r2-${index}`} className={`w-20 h-20 ${item.color}`} />
            ))}
          </motion.div>

          {/* Row 3 - Left to Right */}
          <motion.div
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
            className="flex gap-28 whitespace-nowrap"
          >
            {infiniteIcons.map((item, index) => (
              <item.Icon key={`r3-${index}`} className={`w-28 h-28 ${item.color}`} />
            ))}
          </motion.div>

          {/* Row 4 - Right to Left */}
          <motion.div
            animate={{ x: ["-50%", "0%"] }}
            transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
            className="flex gap-36 whitespace-nowrap ml-24"
          >
            {infiniteIcons.map((item, index) => (
              <item.Icon key={`r4-${index}`} className={`w-16 h-16 ${item.color}`} />
            ))}
          </motion.div>
        </div>

        {/* Vignette */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#050505_100%)] opacity-80" />
      </div>

      <div className="w-full max-w-lg relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5, type: "spring", damping: 20 }}
          className="bg-black/60 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-8 shadow-[0_0_50px_-12px_rgba(0,0,0,0.5)] relative overflow-hidden ring-1 ring-white/5"
        >
          {/* Shine Effect */}
          <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-transparent to-transparent opacity-50 pointer-events-none" />

          {/* Header */}
          <div className="text-center mb-8 relative">
            <div className="inline-block relative mb-4">
              <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center relative group">
                <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-500 opacity-50" />
                <img
                  src={LogoCapital}
                  alt="Grupo Capital Logo"
                  className="w-full h-full object-cover rounded-full relative z-10 border-2 border-white/10 shadow-2xl"
                />
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-red-400 bg-clip-text text-transparent mb-2">Completar Cadastro</h1>
              <p className="text-blue-200/60 text-sm font-medium">Finalize seu perfil para acessar o sistema.</p>
            </motion.div>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-between mb-8 px-2 relative">
            <div className="absolute top-1/2 left-4 right-4 h-0.5 bg-white/5 -z-0 -translate-y-1/2" />
            {steps.map((step, index) => {
              const isActive = index + 1 === currentStep;
              const isCompleted = index + 1 < currentStep;

              return (
                <div key={step.id} className="flex flex-col items-center relative z-10">
                  <motion.div
                    initial={false}
                    animate={{
                      scale: isActive ? 1.1 : 1,
                      backgroundColor: isActive ? 'rgba(59, 130, 246, 1)' : isCompleted ? 'rgba(16, 185, 129, 1)' : 'rgba(255, 255, 255, 0.1)'
                    }}
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 shadow-lg ${isActive || isCompleted ? 'text-white' : 'text-white/40 border border-white/5'}`}
                  >
                    {isCompleted ? <Check className="h-4 w-4" /> : <step.icon className="h-4 w-4" />}
                  </motion.div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold mt-2 transition-colors duration-300 ${isActive ? 'text-blue-400' : isCompleted ? 'text-emerald-400' : 'text-white/20'
                    }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Content Area */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs flex items-center gap-2"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </motion.div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-8 pt-6 border-t border-white/5">
            {currentStep > 1 && (
              <Button
                variant="ghost"
                onClick={handlePrevious}
                className="flex-1 bg-white/5 hover:bg-white/10 text-white border border-white/5 rounded-xl h-11 transition-all"
                disabled={loading}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            )}
            <Button
              onClick={handleNext}
              className={`flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-bold rounded-xl h-11 transition-all shadow-[0_4px_20px_-4px_rgba(59,130,246,0.5)] hover:shadow-[0_8px_25px_-4px_rgba(59,130,246,0.6)] hover:-translate-y-0.5 ${currentStep === 1 ? 'w-full' : ''}`}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {currentStep === steps.length ? 'Finalizar Cadastro' : 'Continuar'}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>

        </motion.div>
      </div>
    </div>
  );
};

export default CompleteProfilePage;
